import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTable, useSortBy, usePagination } from "react-table";
import CustomerDataService from "../services/CustomerService";
import IndentDataService from "../services/IndentService";
import { ApplicationContext } from "./ApplicationContext";
import { formatCurrency } from "./utils";
import { toastService } from "../services/toastService";

const STATUS_COLORS = {
  PENDING: "bg-yellow-100 text-yellow-800",
  ACTIVATED: "bg-green-100 text-green-800",
  DELIVERED: "bg-gray-100 text-gray-700",
  CANCELED: "bg-red-100 text-red-700",
};

const formatDate = (timestamp) =>
  timestamp
    ? new Date(timestamp).toLocaleDateString("sr-RS", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

const CustomerOrdersList = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { userRole } = useContext(ApplicationContext);

  const [customer, setCustomer] = useState(null);
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [code, setCode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    code: "",
    startDate: "",
    endDate: "",
    statuses: [],
  });

  useEffect(() => {
    CustomerDataService.get(customerId)
      .then((res) => setCustomer(res.data))
      .catch(() => toastService.error("Greška pri učitavanju klijenta"));
  }, [customerId]);

  const loadOrders = useCallback(() => {
    setLoading(true);
    const params = {
      customerId: parseInt(customerId),
      page: currentPage,
      size: pageSize,
      sortBy: "creationTime",
      sortDir: "desc",
      ...(appliedFilters.code && { code: appliedFilters.code }),
      ...(appliedFilters.startDate && { dateFrom: appliedFilters.startDate }),
      ...(appliedFilters.endDate && { dateTo: appliedFilters.endDate }),
      ...(appliedFilters.statuses.length > 0 && { status: appliedFilters.statuses }),
    };
    IndentDataService.search(params)
      .then((res) => {
        setIndents(res.data.content || []);
        setTotalPages(res.data.totalPages);
        setTotalElements(res.data.totalElements);
      })
      .catch(() => toastService.error("Greška pri učitavanju porudžbina"))
      .finally(() => setLoading(false));
  }, [customerId, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleSearch = () => {
    setCurrentPage(0);
    setAppliedFilters((prev) => ({ ...prev, code, startDate, endDate }));
  };

  const handleReset = () => {
    setCode("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    setAppliedFilters({ code: "", startDate: "", endDate: "", statuses: [] });
  };

  const handleStatusTab = (statuses) => {
    setCurrentPage(0);
    setAppliedFilters((prev) => ({ ...prev, statuses }));
  };

  const handleActivate = useCallback(
    (indentCode) => {
      IndentDataService.activateIndent(indentCode)
        .then(() => {
          toastService.success("Porudžbina aktivirana");
          loadOrders();
        })
        .catch(() => toastService.error("Greška pri aktivaciji"));
    },
    [loadOrders]
  );

  const handleConfirmDelivery = useCallback(
    (indentCode) => {
      IndentDataService.confirmIndentDelivery(indentCode)
        .then(() => {
          toastService.success("Isporuka potvrđena");
          loadOrders();
        })
        .catch(() => toastService.error("Greška pri potvrdi isporuke"));
    },
    [loadOrders]
  );

  const columns = useMemo(
    () => [
      {
        Header: "#",
        id: "rowIndex",
        Cell: ({ row }) => currentPage * pageSize + row.index + 1,
      },
      {
        Header: "Šifra",
        accessor: "code",
        Cell: ({ value }) => (
          <span className="font-semibold text-sm">{value}</span>
        ),
      },
      {
        Header: "Adresa",
        id: "address",
        Cell: ({ row: { original } }) => (
          <span className="text-sm text-gray-600">
            {original.deliveryAddressCity}, {original.deliveryAddressLocation}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "indentStatus",
        Cell: ({ value }) => (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              STATUS_COLORS[value] || "bg-gray-100 text-gray-700"
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Datum",
        accessor: "creationTime",
        Cell: ({ value }) => (
          <span className="text-sm text-gray-500">{formatDate(value)}</span>
        ),
      },
      // Removed "Iznos" column here
      {
        Header: "Akcije",
        id: "actions",
        Cell: ({ row: { original } }) => (
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate(`/indents/entries/${original.code}`)}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Pregled
            </button>
            {original.indentStatus === "PENDING" &&
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_FAKTURISTA") && (
                <button
                  onClick={() => handleActivate(original.code)}
                  className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  Aktiviraj
                </button>
              )}
            {original.indentStatus === "ACTIVATED" &&
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MAGACIONER") && (
                <button
                  onClick={() => handleConfirmDelivery(original.code)}
                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  Potvrdi isporuku
                </button>
              )}
          </div>
        ),
      },
    ],
    [currentPage, pageSize, userRole, navigate, handleActivate, handleConfirmDelivery]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
  } = useTable(
    { columns, data: indents, manualPagination: true, pageCount: totalPages },
    useSortBy,
    usePagination
  );

  const customerName = customer
    ? customer.customerType === "INFLUENCER"
      ? customer.influencerName
      : customer.nameOfTheLegalEntity
    : "...";
  const isInfluencer = customer?.customerType === "INFLUENCER";
  const activeStatusTab =
    appliedFilters.statuses.length === 1 ? appliedFilters.statuses[0] : "";

  const statusTabs = [
    { label: `Sve (${totalElements})`, value: "" },
    { label: "Pending", value: "PENDING" },
    { label: "Aktivirane", value: "ACTIVATED" },
    { label: "Isporučene", value: "DELIVERED" },
    { label: "Otkazane", value: "CANCELED" },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
        >
          <i className="fas fa-arrow-left" /> Klijenti
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          Porudžbine: {customerName}
          {isInfluencer && (
            <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
              INFLUENCER
            </span>
          )}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {/* Status tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          {statusTabs.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => handleStatusTab(value === "" ? [] : [value])}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeStatusTab === value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-5 flex-wrap items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Šifra porudžbine
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Pretraži šifru..."
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Datum od</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Datum do</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Pretraži
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-10 text-gray-400">Učitavanje...</div>
        ) : indents.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            Nema porudžbina za ovog klijenta
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full border-collapse">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    className="bg-gray-50 border-b-2 border-gray-200"
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {column.render("Header")}
                        {column.isSorted && (
                          <span>{column.isSortedDesc ? " ▼" : " ▲"}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {tablePage.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="px-4 py-3">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Veličina stranice:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(0);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {[10, 20, 30, 40, 50].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <span className="ml-2">
              Prikazano{" "}
              {totalElements === 0
                ? 0
                : currentPage * pageSize + 1}
              –{Math.min((currentPage + 1) * pageSize, totalElements)} od{" "}
              {totalElements}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
            >
              ◀◀
            </button>
            <button
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 0}
              className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
            >
              ◀
            </button>
            <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
              {currentPage + 1}
            </span>
            <span className="text-sm text-gray-400">/ {totalPages}</span>
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage >= totalPages - 1}
              className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
            >
              ▶
            </button>
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
            >
              ▶▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrdersList;
